import { Dispatch, SetStateAction } from 'react';
import { DocumentsTable } from './DocumentsTable/DocumentsTable';
import { EmptyDocuments } from './EmptyDocuments/EmptyDocuments';
import { EditDocument } from '@dealstack/frontend/shared/components/Documents/DocumentDrawer/types';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { UploadEntityDetails } from '@dealstack/frontend/shared/components/Documents/types';
import { GetDocumentsRes } from '@dealstack/shared/lib/generated';
import {
	DocumentLinkTarget,
	DocumentsRes,
} from '@dealstack/frontend/shared/dataExchange/services/shared/types';
import { DocumentsModal } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/components/DocumentsModal';
import { UploadDocuments } from '@dealstack/frontend/shared/components/Documents/UploadDocuments/UploadDocuments';
import { MantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';

interface Props {
	dealId: string;
	target: DocumentLinkTarget;
	uploadEntityDetails?: UploadEntityDetails[];
	isUploadActive: boolean;
	setIsUploadActive: Dispatch<SetStateAction<boolean>>;
	setEditDocument: Dispatch<SetStateAction<EditDocument>>;
	showFilters: boolean;
	columns: ExtendedColumnDef<DocumentsRes>[];
	mantineTableDataOperations: MantineTableDataOperations;
	documents?: GetDocumentsRes;
	isDocumentsLoading: boolean;
	hasFilterApplied: boolean;
	hasSearchApplied: boolean;
	previewDocument:
		| {
				downloadUrl: string;
				fileExtension: string;
				fileName: string;
		  }
		| undefined;
	setPreviewDocument: (
		previewDocument:
			| {
					downloadUrl: string;
					fileExtension: string;
					fileName: string;
			  }
			| undefined
	) => void;
}

export const Documents = ({
	dealId,
	target,
	isUploadActive,
	setIsUploadActive,
	setEditDocument,
	showFilters,
	uploadEntityDetails,
	columns,
	mantineTableDataOperations,
	documents,
	isDocumentsLoading,
	hasFilterApplied,
	hasSearchApplied,
	previewDocument,
	setPreviewDocument,
}: Props) => {
	const tCapManage = useTranslationCapManage();

	const hasDocuments = !!documents?.documents.length;

	if (isUploadActive) {
		return (
			<DocumentsModal
				isOpen={isUploadActive}
				onClose={() => setIsUploadActive(false)}
				title={tCapManage('document.upload_state.header')}
			>
				<UploadDocuments
					dealId={dealId}
					target={target}
					setIsUploadActive={setIsUploadActive}
					uploadEntityDetails={uploadEntityDetails}
				/>
			</DocumentsModal>
		);
	}

	if (!hasDocuments && !hasSearchApplied && !hasFilterApplied && !isDocumentsLoading) {
		return (
			<EmptyDocuments
				setIsUploadActive={setIsUploadActive}
				headerText={tCapManage('document.empty_state.header')}
			/>
		);
	}

	return (
		<DocumentsTable
			isLoading={isDocumentsLoading}
			showFilters={showFilters}
			onRowClick={(row) =>
				setEditDocument({
					documentId: row.original.documentId,
					target,
				})
			}
			tableDataOperations={mantineTableDataOperations}
			documentsPayload={documents}
			columns={columns}
			previewDocument={previewDocument}
			setPreviewDocument={setPreviewDocument}
		/>
	);
};
